/** Photo gallery editor for one adoption listing (user-owned, ≤5). Mirrors PetPhotosSection. */
import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AuthImage } from '@/components/auth-image';
import { ApiError } from '@/lib/api';
import { use_listing_photos, use_listing_photo_mutations } from '@/features/adoption-listing/hooks';

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX = 5;

export function AdoptionPhotoEditor({ listing_id }: { listing_id: string }) {
  const photos_q = use_listing_photos(listing_id);
  const { upload, remove } = use_listing_photo_mutations(listing_id);
  const file_input = useRef<HTMLInputElement>(null);
  const [error, set_error] = useState<string | null>(null);
  const photos = photos_q.data ?? [];

  async function handle_select(e: React.ChangeEvent<HTMLInputElement>) {
    set_error(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await upload.mutateAsync(file);
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Upload failed');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Label>Photos <span className="text-xs font-normal text-muted-foreground">({photos.length}/{MAX})</span></Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => file_input.current?.click()}
          disabled={upload.isPending || photos.length >= MAX}
          data-testid="adoption-photo-upload"
        >
          {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          Add photo
        </Button>
        <input ref={file_input} type="file" accept={PHOTO_ACCEPT} className="hidden" data-testid="adoption-photo-input" onChange={handle_select} />
      </div>

      {error && <p className="text-xs text-destructive" data-testid="adoption-photo-error">{error}</p>}

      {photos.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="adoption-photos-empty">
          Add a clear photo or two — it hugely helps this pet find a home.
        </p>
      ) : (
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {photos.map((p) => (
            <li key={p.id} data-testid={`adoption-photo-${p.id}`} className="group relative overflow-hidden rounded-input border-2 border-border/60 bg-card shadow-clay-sm">
              <AuthImage path={`${p.file_path}?variant=thumb`} alt="Pet" className="aspect-square w-full object-cover" />
              <div className="flex justify-end p-1.5">
                <button
                  type="button"
                  onClick={() => remove.mutate(p.id)}
                  disabled={remove.isPending}
                  aria-label="Delete photo"
                  data-testid={`adoption-photo-delete-${p.id}`}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
